/**
 * General Ledger Report Screen - Using Report Engine
 * Standardized layout with database-driven columns, export, and column settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FileSearch, Calendar, Search } from 'lucide-react';
import { ReportLayout, SummaryCard } from '@/components/report-engine';
import api from '@/services/api';

const GeneralLedgerReportScreen = ({ theme }) => {
  const isDark = theme === 'dark';
  
  // Data state
  const [accounts, setAccounts] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter state
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [fromDate, setFromDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Summary state
  const [openingBalance, setOpeningBalance] = useState(0);
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);

  // Fetch accounts on mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  // GL Ledger pre-filter — applied after accounts are loaded (set by "Open Ledger" from voucher grids)
  useEffect(() => {
    if (accounts.length === 0) return;
    const prefilterStr = sessionStorage.getItem('glLedgerPrefilter');
    if (!prefilterStr) return;
    try {
      const prefilter = JSON.parse(prefilterStr);
      const match = accounts.find(a => String(a.GLId) === String(prefilter.accountId));
      if (match) setSelectedAccount(match);
    } catch { /* ignore malformed */ }
    sessionStorage.removeItem('glLedgerPrefilter');
  }, [accounts]);

  // Fetch ledger data when account or date changes
  useEffect(() => {
    if (selectedAccount) {
      fetchLedgerData();
    }
  }, [selectedAccount, fromDate, toDate]);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/api/vouchers/accounts/all');
      if (response.data.success) {
        setAccounts(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchLedgerData = async () => {
    if (!selectedAccount) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/api/report-engine/data/GL_REPORT', {
        page: 1,
        pageSize: 10000,
        filters: {
          accountId: selectedAccount.GLId,
          fromDate,
          toDate
        }
      });
      
      if (response.data.success) {
        const rows = response.data.data.rows || [];
        setData(rows);
        
        // Calculate running balance
        let runningBalance = openingBalance;
        const processedRows = rows.map(row => {
          runningBalance += (parseFloat(row.DrAmount) || 0) - (parseFloat(row.CrAmount) || 0);
          return { ...row, Balance: runningBalance };
        });
        
        setData(processedRows);
        
        // Calculate totals
        const totDr = rows.reduce((sum, r) => sum + (parseFloat(r.DrAmount) || 0), 0);
        const totCr = rows.reduce((sum, r) => sum + (parseFloat(r.CrAmount) || 0), 0);
        setTotalDebit(totDr);
        setTotalCredit(totCr);
        setClosingBalance(openingBalance + totDr - totCr);
      }
    } catch (err) {
      console.error('Error fetching ledger data:', err);
      setError('Failed to load ledger data');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(() => {
    if (selectedAccount) {
      fetchLedgerData();
    }
  }, [selectedAccount, fromDate, toDate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Filter accounts by search
  const filteredAccounts = accounts.filter(a =>
    a.GLName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.GLId?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Summary cards
  const summaryCards = selectedAccount ? [
    { 
      label: 'Opening Balance', 
      value: formatCurrency(Math.abs(openingBalance)),
      suffix: openingBalance >= 0 ? 'Dr' : 'Cr',
      variant: openingBalance >= 0 ? 'success' : 'danger'
    },
    { label: 'Total Debit', value: formatCurrency(totalDebit), variant: 'success' },
    { label: 'Total Credit', value: formatCurrency(totalCredit), variant: 'danger' },
    { 
      label: 'Closing Balance', 
      value: formatCurrency(Math.abs(closingBalance)),
      suffix: closingBalance >= 0 ? 'Dr' : 'Cr',
      variant: closingBalance >= 0 ? 'success' : 'danger'
    }
  ] : [];

  // Toolbar filters
  const FiltersToolbar = (
    <div className="flex flex-wrap items-center gap-3">
      {/* Date Range */}
      <div className="flex items-center gap-2">
        <Calendar size={16} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          data-testid="gl-from-date"
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        />
        <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          data-testid="gl-to-date"
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        />
      </div>
    </div>
  );

  // Left toolbar - Account selector
  const AccountSelector = (
    <div className="relative w-72">
      <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
      <input
        type="text"
        placeholder="Search account..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        data-testid="gl-account-search"
        className={`w-full pl-10 pr-4 py-1.5 rounded-lg border text-sm ${
          isDark ? 'bg-[#151515] border-[#333] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
        }`}
      />
    </div>
  );

  // Custom content - Account list sidebar + report grid
  const CustomContent = (
    <div className="flex-1 flex overflow-hidden">
      {/* Account List Sidebar */}
      <div className={`w-80 shrink-0 border-r overflow-hidden flex flex-col ${isDark ? 'border-[#222] bg-[#0e0e10]' : 'border-gray-200 bg-white'}`}>
        {/* Search header */}
        <div className={`shrink-0 p-3 border-b ${isDark ? 'border-[#222]' : 'border-gray-100'}`}>
          <div className="relative">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search account..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="gl-account-search-sidebar"
              className={`w-full pl-10 pr-4 py-2 rounded-lg border text-sm ${
                isDark ? 'bg-[#151515] border-[#333] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
              }`}
            />
          </div>
          <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
            {filteredAccounts.length} accounts
          </p>
        </div>
        
        {/* Account list */}
        <div className="flex-1 overflow-y-auto p-2">
          {filteredAccounts.slice(0, 100).map((account) => (
            <button
              key={account.GLId}
              onClick={() => setSelectedAccount(account)}
              data-testid={`account-${account.GLId}`}
              className={`w-full text-left px-3 py-2 rounded-lg mb-1 transition-colors ${
                selectedAccount?.GLId === account.GLId
                  ? isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-50 text-indigo-600'
                  : isDark ? 'text-slate-400 hover:bg-white/5 hover:text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <p className="text-sm font-medium truncate">{account.GLName}</p>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{account.GLId}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!selectedAccount ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <FileSearch className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-slate-700' : 'text-gray-300'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Select an Account
              </h3>
              <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                Choose an account from the list to view its ledger
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className={`shrink-0 p-4 border-b ${isDark ? 'border-[#222]' : 'border-gray-100'}`}>
              <div className="grid grid-cols-4 gap-4">
                {summaryCards.map((card, index) => (
                  <div key={index} className={`p-4 rounded-xl border ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-gray-200'}`}>
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{card.label}</p>
                    <p className={`text-xl font-bold ${card.variant === 'success' ? 'text-green-500' : card.variant === 'danger' ? 'text-red-500' : isDark ? 'text-white' : 'text-gray-900'}`}>
                      {card.value} {card.suffix && <span className="text-sm font-normal">{card.suffix}</span>}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Transactions Table */}
            <div className="flex-1 overflow-auto p-4">
              <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-gray-200'}`}>
                <table className="w-full">
                  <thead className={isDark ? 'bg-[#0e0e10]' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Date</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Voucher</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Type</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Particulars</th>
                      <th className={`px-4 py-3 text-right text-xs font-semibold uppercase text-green-500`}>Debit</th>
                      <th className={`px-4 py-3 text-right text-xs font-semibold uppercase text-red-500`}>Credit</th>
                      <th className={`px-4 py-3 text-right text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Opening Balance Row */}
                    <tr className={`border-b ${isDark ? 'border-[#1a1a1a] bg-indigo-500/5' : 'border-gray-50 bg-indigo-50'}`}>
                      <td colSpan={4} className={`px-4 py-3 text-sm font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                        Opening Balance
                      </td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className={`px-4 py-3 text-sm font-bold text-right ${openingBalance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {formatCurrency(Math.abs(openingBalance))} {openingBalance >= 0 ? 'Dr' : 'Cr'}
                      </td>
                    </tr>
                    
                    {/* Loading state */}
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center">
                          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Loading transactions...</p>
                        </td>
                      </tr>
                    ) : data.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center">
                          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>No transactions found for this period</p>
                        </td>
                      </tr>
                    ) : (
                      data.map((t, idx) => (
                        <tr key={idx} className={`border-b ${isDark ? 'border-[#1a1a1a] hover:bg-white/5' : 'border-gray-50 hover:bg-gray-50'}`}>
                          <td className={`px-4 py-3 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t.DocDateFormatted}</td>
                          <td className={`px-4 py-3 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.DocId}</td>
                          <td className={`px-4 py-3 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{t.VoucherType}</td>
                          <td className={`px-4 py-3 text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t.Particulars}</td>
                          <td className="px-4 py-3 text-sm font-medium text-right text-green-500">
                            {parseFloat(t.DrAmount) > 0 ? formatCurrency(t.DrAmount) : ''}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-right text-red-500">
                            {parseFloat(t.CrAmount) > 0 ? formatCurrency(t.CrAmount) : ''}
                          </td>
                          <td className={`px-4 py-3 text-sm font-medium text-right ${t.Balance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {formatCurrency(Math.abs(t.Balance))} {t.Balance >= 0 ? 'Dr' : 'Cr'}
                          </td>
                        </tr>
                      ))
                    )}
                    
                    {/* Closing Balance Row */}
                    <tr className={`${isDark ? 'bg-indigo-500/5' : 'bg-indigo-50'}`}>
                      <td colSpan={4} className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                        Closing Balance
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-right text-green-500">{formatCurrency(totalDebit)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-right text-red-500">{formatCurrency(totalCredit)}</td>
                      <td className={`px-4 py-3 text-sm font-bold text-right ${closingBalance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {formatCurrency(Math.abs(closingBalance))} {closingBalance >= 0 ? 'Dr' : 'Cr'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <ReportLayout
      reportCode="GL_REPORT"
      reportTitle="General Ledger Report"
      reportSubtitle={selectedAccount ? selectedAccount.GLName : 'Select an account to view ledger'}
      reportIcon={<FileSearch size={20} />}
      data={data}
      loading={loading && !!selectedAccount}
      error={error}
      onRefresh={handleRefresh}
      theme={theme}
      filters={FiltersToolbar}
    >
      {CustomContent}
    </ReportLayout>
  );
};

export default GeneralLedgerReportScreen;
